import { createRouter, createWebHistory } from 'vue-router';
import {
    getSettings,
    getTrimData,
    getTrims,
    getUser,
    getUserCreatedTrims,
    getUsers,
    getUserTrims,
} from './beforeEnter';

import PageHome from '../views/home/PageHome';
import TrimEditorMain from '../views/trim-editor/TrimEditorMain';
import ResetPassword from '../views/authentication/ResetPassword';
import HandoffView from '../views/authentication/HandoffView';
import UserProfile from '../views/home/admin/users/userPage/UserProfile';
import UserStatistic from '../views/home/admin/users/userPage/UserStatistic';
import UserCreatedTrimsTab from '../views/home/admin/users/userPage/UserCreatedTrimsTab';
import RestartServerTab from '../views/home/admin/settings/restart-server/RestartServerTab';
import ExportSettingsTab from '../views/home/admin/settings/export-settings/ExportSettingsTab';
import UnauthorizedAccess from '../views/security/UnauthorizedAccess';

import store from '../store';


const routes = [
    {
        path: '/trims',
        name: 'trims',
        component: PageHome,
        props: true,
        beforeEnter: getTrims,
    },
    {
        path: '/',
        name: 'home',
        component: PageHome,
        props: { autoOpenAddTrim: true },
        beforeEnter: getTrims,
    },
    {
        path: '/my-trims',
        name: 'my-trims',
        component: PageHome,
        props: true,
        beforeEnter: getUserTrims,
    },
    {
        path: '/settings',
        name: 'settings',
        component: PageHome,
        props: true,
        redirect: {
            name: 'settingsServer'
        },
        children: [
            {
                path: '/server',
                name: 'settingsServer',
                component: RestartServerTab,
                props: true,
            },
            {
                path: '/export',
                name: 'settingsExport',
                component: ExportSettingsTab,
                props: true,
                beforeEnter: getSettings,
            },
        ],
    },
    {
        path: '/users',
        name: 'users',
        component: PageHome,
        props: true,
        beforeEnter: getUsers,
    },
    {
        path: '/users/:userName',
        name: 'user',
        component: PageHome,
        props: true,
        beforeEnter: getUser,
        children: [
            {
                path: 'profile',
                alias: '',
                name: 'userProfile',
                component: UserProfile,
            },
            {
                path: 'trims',
                name: 'userCreatedTrims',
                component: UserCreatedTrimsTab,
                beforeEnter: getUserCreatedTrims,
            },
            {
                path: 'statistic',
                name: 'userStatistic',
                component: UserStatistic,
            },
        ],
    },
    {
        path: '/editor/:trimName',
        name: 'editor',
        component: TrimEditorMain,
        props: true,
        beforeEnter: getTrimData,
    },
    {
        path: '/reset-password',
        name: 'resetPassword',
        component: ResetPassword,
        props: true,
    },
    {
        path: '/validate-handoff',
        name: 'validateHandoff',
        component: HandoffView,
        props: true,
        // No authentication middleware - this route handles its own auth
        meta: {
            skipAuth: true
        }
    },
    {
        path: '/unauthorized',
        name: 'unauthorized', 
        component: UnauthorizedAccess,
        meta: {
            skipAuth: true // Don't require auth to view unauthorized page
        }
    },
    {
        path: '/:pathMatch(.*)*',
        name: '404',
        beforeEnter: (to, from, next) => {
            next({path: '/'});
        },
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        document.getElementById('app').scrollIntoView({ behavior: 'smooth' });
    }
});

router.beforeEach(async (to, from, next) => {
    
    // Skip authentication for routes that don't require it
    if (to.meta?.skipAuth) {
        next();
        return;
    }

    // Check for valid handoff session with timeout protection
    try {
        console.log(`🛡️ Router guard: Validating authentication for route: ${to.path}`);
        
        // Add timeout to prevent hanging on session validation
        const validationPromise = store.dispatch('authentication/validateCurrentSession');
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Session validation timeout')), 10000) // 10 second timeout
        );
        
        const isAuthenticated = await Promise.race([validationPromise, timeoutPromise]);
        
        if (!isAuthenticated) {
            console.log(`🚫 Router guard: Authentication failed for route: ${to.path}, redirecting to unauthorized`);
            next({ name: 'unauthorized' });
            return;
        }
        
        console.log(`✅ Router guard: Authentication successful for route: ${to.path}`);
        
        // Continue with existing URL redirect logic
        const url = to.query['url'];
        if (url) {
            const urlParts = url.split('?');
            const path = urlParts[0];
            const queryString = urlParts[1] || '';
            const urlParams = new URLSearchParams(queryString);
            const query = Object.fromEntries(urlParams);
            next({path, query});
            return;
        }

        next();
        
    } catch (error) {
        console.error(`❌ Router guard: Authentication validation failed for route: ${to.path}`, error);
        next({ name: 'unauthorized' });
    }
});

export default router;